import { Platform } from 'quasar'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { StatusBar, Style } from '@capacitor/status-bar'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { CapacitorSquare } from '@zuc0001/capacitor-square'
import { App } from '@capacitor/app'
import Bus from 'src/helpers/bus'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { otaState, restartToUpdate, restoreRouteAfterUpdate } from 'src/helpers/otaUpdate'

// leave the export, even if you don't use it
export default async ({ app, router }) => {
  if (!Platform.is.capacitor) {
    return
  }

  // Must be the first thing that runs.
  //
  // Capgo gives a freshly installed bundle `appReadyTimeout` (10s by default) to
  // confirm it booted. If notifyAppReady() has not been called by then, checkRevert()
  // marks the bundle ERROR, rolls back to the last good one, and every later
  // installNext() skips it -- `!next.isErrorStatus()` is an install precondition. The
  // device then re-downloads the same bundle forever and never installs it.
  //
  // Anything awaited ahead of this eats into that budget, so nothing may go before it.
  try {
    await CapacitorUpdater.notifyAppReady()
  }
  catch (error) {
    console.error('CapacitorUpdater.notifyAppReady() failed', error)
  }

  await restoreRouteAfterUpdate(router)

  // Feed the shared OTA state so the full-screen OtaUpdate overlay (App.vue)
  // shows the download, then restart straight into the new bundle. Updates are
  // forced: every device must run the same version.
  await CapacitorUpdater.addListener('download', ({ percent, bundle }) => {
    otaState.status = 'downloading'
    otaState.percent = Math.round(percent)
    otaState.version = bundle.version
  })

  await CapacitorUpdater.addListener('downloadComplete', ({ bundle }) => {
    otaState.status = 'installing'
    otaState.percent = 100
    otaState.version = bundle.version
  })

  // Fires once the bundle has passed its checksum and been staged as next
  // (downloadComplete fires before that check), so this is the safe point to
  // swap to it. Also delivered on foreground for a download that finished
  // while the app was in the background.
  await CapacitorUpdater.addListener('updateAvailable', ({ bundle }) => {
    otaState.status = 'installing'
    otaState.version = bundle.version
    otaState.bundleId = bundle.id
    restartToUpdate()
  })

  // Not fatal: the plugin retries the download on next launch.
  await CapacitorUpdater.addListener('downloadFailed', ({ version }) => {
    console.error('CapacitorUpdater download failed', version)
    otaState.status = 'failed'
  })

  // #### not necessary for now
  // If not production, attempt to change to "test-X" channel
  // Production will use default channels set in Capgo
  // if (process.env.NODE_ENV !== 'production') {
  //   CapacitorUpdater.setChannel({
  //     channel: generateChannelStr(),
  //     triggerAutoUpdate: true
  //   })
  // }

  app.config.globalProperties.$nativeSecureStorage = SecureStoragePlugin

  // Quasar only adds this class on iOS (framework.config.capacitor.iosStatusBarPadding).
  // Since Capacitor 8 the Android WebView is edge-to-edge too, so the same
  // env(safe-area-inset-*) padding for dialogs, footers and the drawer applies.
  if (Platform.is.android) {
    document.body.classList.add('q-ios-padding')
  }

  // iOS only. Since Capacitor 7 the StatusBar plugin also honours
  // setBackgroundColor/setOverlaysWebView on iOS, and on Android 15+ they are
  // no-ops (edge-to-edge is enforced; Capacitor 8's System Bars plugin exposes
  // the insets as --safe-area-inset-* instead), so neither is called anymore.
  if (Platform.is.ios) {
    StatusBar.setStyle({ style: Style.Default })

    // Unimplemented on Android (rejects with "not implemented").
    // None: the webview keeps its full height under the keyboard, so the translucent
    // iOS keyboard shows the app through it instead of a bare window. The CSS pads the
    // scroll containers by --keyboard-height so fields stay reachable, and the report
    // generator scrolls inside its page container with the document pinned, so
    // nothing can move under the fixed header (Layout.vue innerScroll).
    Keyboard.setResizeMode({
      mode: KeyboardResize.None
    })

    // Capacitor hides the native accessory bar by default, which leaves inputs with
    // only a Return key and no way to put the keyboard away. Showing it gives every
    // keyboard the iOS "< > Done" strip, number pads and the rich text editor included.
    Keyboard.setAccessoryBarVisible({ isVisible: true })
  }

  // Flag the body while the keyboard is open so sticky footers unpin and flow after
  // the content, and publish the keyboard height for the iOS padding rules
  // (see the .keyboard-visible rules in app.scss). Android resizes the webview
  // natively, so the height is only consumed by the platform-ios rules.
  Keyboard.addListener('keyboardWillShow', (info) => {
    document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight || 0}px`)
    document.body.classList.add('keyboard-visible')
  })
  Keyboard.addListener('keyboardDidShow', () => {
    // iOS: WebKit revealed the field at focus time against the full-height webview;
    // now that the keyboard covers its bottom, bring the field into the padded part
    // above it (the page container or dialog card that scrolls it).
    const el = document.activeElement
    if (Platform.is.ios && el && el.matches('input, textarea, [contenteditable]')) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  })
  Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.style.setProperty('--keyboard-height', '0px')
    document.body.classList.remove('keyboard-visible')
  })

  CapacitorSquare.initApp({
    applicationId: process.env.SQUARE_KEY
  }).catch(error => {
    console.error(error)
  })

  App.addListener('appUrlOpen', (data) => {
    if (data.url.toLowerCase().startsWith('miraapp://square-callback')) {
      Bus.emit('square:callback', data)
    }
  })

  // Listen for completed payments
  CapacitorSquare.addListener('transactionComplete', callback => {
    Bus.emit('square:callback', callback)
  })

  // Listen for failed payments
  CapacitorSquare.addListener('transactionFailed', callback => {
    Bus.emit('square:callback', callback)
  })
}

// Capgo channel string will be in format:
// prod-ios, prod-android, test-ios, test-android
// function generateChannelStr () {
//   const channel = process.env.NODE_ENV === 'production' ? 'prod' : 'test'
//   const platform = Platform.is.ios ? 'ios' : (Platform.is.android ? 'android' : 'unknown')
//
//   return `${channel}-${platform}`
// }
